/**
 * Complete Production-Grade Reports & Business Intelligence Route — Module 13.
 * Integrated directly with live Supabase DB order records, product inventory, and customer metrics.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { siteConfig } from "@/config/site";
import {
  TrendingUp,
  DollarSign,
  ShoppingBag,
  BarChart3,
  Download,
  FileSpreadsheet,
  Calendar,
  Layers,
  Users,
  ShieldCheck,
  Check,
  ArrowUpRight,
  PieChart as PieChartIcon,
} from "lucide-react";
import {
  fetchAdminOrdersFromSupabase,
  type AdminOrderRecord,
  type OrderItemDetail,
} from "@/features/admin/orders/store/admin-orders-store";
import { getLiveProducts, type Product } from "@/lib/products";
import { fetchAdminProductsFromSupabase } from "@/features/admin/products/store/admin-products-store";
import { DataTable, type ColumnDef } from "@/components/admin/common/data-table";

type ReportProduct = Pick<
  Product,
  "id" | "name" | "category" | "price" | "stockQuantity" | "inStock"
> & { stock?: number };

interface ProductPerformanceRow {
  name: string;
  category: string;
  unitsSold: number;
  revenue: number;
  price: number;
}

export const Route = createFileRoute("/admin/reports")({
  head: () => ({
    meta: [{ title: `Business Intelligence — Admin — ${siteConfig.name}` }],
  }),
  component: AdminReportsPage,
});

type TimePeriod = "TODAY" | "THIS_WEEK" | "THIS_MONTH" | "THIS_YEAR" | "ALL_TIME";
type ReportTab = "SALES" | "PRODUCTS" | "TAX_GST" | "INVENTORY" | "CUSTOMERS";

function AdminReportsPage() {
  const [orders, setOrders] = useState<AdminOrderRecord[]>([]);
  const [productsList, setProductsList] = useState<ReportProduct[]>(() => getLiveProducts());
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("ALL_TIME");
  const [activeTab, setActiveTab] = useState<ReportTab>("SALES");
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminOrdersFromSupabase().then((data) => {
      if (data && data.length > 0) setOrders(data);
    });
    fetchAdminProductsFromSupabase().then((data) => {
      if (data && data.length > 0) setProductsList(data);
    });
  }, []);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  // Filter orders by selected time period
  const filteredOrders = useMemo(() => {
    const now = new Date();
    return orders.filter((o) => {
      const date = new Date(o.date);
      if (timePeriod === "TODAY") {
        return date.toDateString() === now.toDateString();
      }
      if (timePeriod === "THIS_WEEK") {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(now.getDate() - 7);
        return date >= oneWeekAgo;
      }
      if (timePeriod === "THIS_MONTH") {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }
      if (timePeriod === "THIS_YEAR") {
        return date.getFullYear() === now.getFullYear();
      }
      return true; // ALL_TIME
    });
  }, [orders, timePeriod]);

  // Overall Financial KPIs
  const kpis = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((acc, o) => acc + (o.total || 0), 0);
    const deliveredCount = filteredOrders.filter((o) => o.status === "DELIVERED").length;
    const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
    const gstLiability = Math.round(totalRevenue * 0.18);
    const totalStockUnits = productsList.reduce(
      (acc, p) => acc + (p.stockQuantity ?? p.stock ?? (p.inStock ? 50 : 0)),
      0,
    );
    const inventoryValuation = productsList.reduce(
      (acc, p) => acc + p.price * (p.stockQuantity ?? p.stock ?? (p.inStock ? 50 : 0)),
      0,
    );

    return {
      totalOrders,
      totalRevenue,
      deliveredCount,
      avgOrderValue,
      gstLiability,
      totalStockUnits,
      inventoryValuation,
    };
  }, [filteredOrders, productsList]);

  // Grouped Product Sales Breakdown
  const productPerformance = useMemo(() => {
    const map = new Map<
      string,
      { name: string; category: string; unitsSold: number; revenue: number; price: number }
    >();

    productsList.forEach((p) => {
      map.set(p.id, {
        name: p.name,
        category: p.category,
        unitsSold: 0,
        revenue: 0,
        price: p.price,
      });
    });

    filteredOrders.forEach((o) => {
      o.items.forEach((item: OrderItemDetail & { price?: number; quantity?: number }) => {
        const existing = map.get(item.id) || {
          name: item.name,
          category: "Botanical Plant",
          unitsSold: 0,
          revenue: 0,
          price: item.unitPrice || item.price || 99,
        };
        const qty = item.qty || item.quantity || 1;
        const price = item.unitPrice || item.price || 99;
        existing.unitsSold += qty;
        existing.revenue += price * qty;
        map.set(item.id, existing);
      });
    });

    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [filteredOrders, productsList]);

  // GST & Tax Breakdown Rows
  const gstBreakdown = useMemo(() => {
    const taxableAmount = Math.round(kpis.totalRevenue / 1.18);
    const totalGst = kpis.totalRevenue - taxableAmount;
    const cgst = Math.round(totalGst / 2);
    const sgst = Math.round(totalGst / 2);

    return {
      grossRevenue: kpis.totalRevenue,
      taxableAmount,
      totalGst,
      cgst,
      sgst,
    };
  }, [kpis.totalRevenue]);

  // Export CSV Handler
  const handleExportCSV = () => {
    let csvContent = "";

    if (activeTab === "SALES") {
      csvContent = "Order ID,Customer Name,Total Amount,Status,Payment Method,Date\n";
      filteredOrders.forEach((o) => {
        csvContent += `"${o.id}","${o.customerName}",${o.total},"${o.status}","${o.paymentMethod}","${o.date}"\n`;
      });
    } else if (activeTab === "PRODUCTS") {
      csvContent = "Product Name,Category,Units Sold,Gross Revenue,Unit Price\n";
      productPerformance.forEach((p) => {
        csvContent += `"${p.name}","${p.category}",${p.unitsSold},${p.revenue},${p.price}\n`;
      });
    } else if (activeTab === "TAX_GST") {
      csvContent = "Gross Sales,Taxable Sales,CGST (9%),SGST (9%),Total GST Liability (18%)\n";
      csvContent += `${gstBreakdown.grossRevenue},${gstBreakdown.taxableAmount},${gstBreakdown.cgst},${gstBreakdown.sgst},${gstBreakdown.totalGst}\n`;
    } else {
      csvContent = "Product Name,Category,Price,Estimated Valuation\n";
      productsList.forEach((p) => {
        csvContent += `"${p.name}","${p.category}",${p.price},${p.price * 100}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `verdant_${activeTab.toLowerCase()}_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    triggerToast(`Exported ${activeTab} report to CSV successfully.`);
  };

  // Product Table Columns
  const productColumns: ColumnDef<ProductPerformanceRow>[] = [
    {
      id: "name",
      header: "Botanical Product",
      accessorKey: "name",
      cell: (row) => (
        <div>
          <p className="font-semibold text-xs text-foreground">{row.name}</p>
          <p className="text-[10px] text-muted-foreground">{row.category}</p>
        </div>
      ),
      sortable: true,
    },
    {
      id: "unitsSold",
      header: "Units Sold",
      accessorKey: "unitsSold",
      cell: (row) => <span className="font-mono text-xs font-bold">{row.unitsSold}</span>,
      sortable: true,
    },
    {
      id: "price",
      header: "Unit Price",
      accessorKey: "price",
      cell: (row) => <span className="font-mono text-xs text-muted-foreground">₹{row.price}</span>,
      sortable: true,
    },
    {
      id: "revenue",
      header: "Gross Sales Revenue",
      accessorKey: "revenue",
      cell: (row) => (
        <span className="font-mono text-xs font-bold text-emerald-800">
          ₹{row.revenue.toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="space-y-6 pb-16">
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-xs font-semibold px-4 py-3 rounded-md shadow-xl flex items-center gap-2 animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-emerald-800 font-bold uppercase tracking-wider">
            Business Intelligence & Financial Analytics
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground font-bold">
            Reports & Insights
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Live sales data, GST tax breakdown, product velocity, and multi-format CSV exporter.
          </p>
        </div>

        {/* Time Period Filter & Export CSV Button */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
            className="h-10 border border-border bg-background px-3 text-xs font-bold rounded-xl outline-none focus:border-emerald-800 cursor-pointer shadow-2xs"
          >
            <option value="TODAY">Today</option>
            <option value="THIS_WEEK">This Week</option>
            <option value="THIS_MONTH">This Month</option>
            <option value="THIS_YEAR">This Year</option>
            <option value="ALL_TIME">All Time</option>
          </select>

          <button
            onClick={handleExportCSV}
            className="inline-flex min-h-10 items-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white px-4 text-xs font-bold uppercase tracking-wider rounded-xl transition-colors shadow-xs"
          >
            <FileSpreadsheet className="h-4 w-4" /> Export CSV Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card border border-border p-4 rounded-2xl shadow-2xs">
          <span className="eyebrow text-[10px] text-muted-foreground block font-bold uppercase">
            Total Revenue
          </span>
          <span className="font-display text-2xl font-bold text-emerald-800">
            ₹{kpis.totalRevenue.toLocaleString("en-IN")}
          </span>
          <span className="text-[11px] text-muted-foreground block mt-1 font-medium">
            From {kpis.totalOrders} total customer orders
          </span>
        </div>

        <div className="bg-card border border-border p-4 rounded-2xl shadow-2xs">
          <span className="eyebrow text-[10px] text-muted-foreground block font-bold uppercase">
            Average Order Value (AOV)
          </span>
          <span className="font-display text-2xl font-bold text-foreground">
            ₹{kpis.avgOrderValue}
          </span>
          <span className="text-[11px] text-emerald-700 block mt-1 font-bold">
            {kpis.deliveredCount} Orders Delivered
          </span>
        </div>

        <div className="bg-card border border-border p-4 rounded-2xl shadow-2xs">
          <span className="eyebrow text-[10px] text-muted-foreground block font-bold uppercase">
            GST Tax Liability (18%)
          </span>
          <span className="font-display text-2xl font-bold text-amber-700">
            ₹{kpis.gstLiability.toLocaleString("en-IN")}
          </span>
          <span className="text-[11px] text-muted-foreground block mt-1 font-medium">
            CGST 9% + SGST 9% calculation
          </span>
        </div>

        <div className="bg-card border border-border p-4 rounded-2xl shadow-2xs">
          <span className="eyebrow text-[10px] text-muted-foreground block font-bold uppercase">
            Catalog Inventory Value
          </span>
          <span className="font-display text-2xl font-bold text-stone-900">
            ₹{kpis.inventoryValuation.toLocaleString("en-IN")}
          </span>
          <span className="text-[11px] text-muted-foreground block mt-1 font-medium">
            Across {productsList.length} botanical products
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-3 text-xs uppercase tracking-wider overflow-x-auto">
        {[
          { id: "SALES", label: "Sales & Orders", icon: TrendingUp },
          { id: "PRODUCTS", label: "Product Performance", icon: BarChart3 },
          { id: "TAX_GST", label: "GST & Tax Summary", icon: DollarSign },
          { id: "INVENTORY", label: "Inventory Valuation", icon: Layers },
        ].map((t) => {
          const IconComponent = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as ReportTab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] font-bold ${
                activeTab === t.id
                  ? "bg-emerald-800 text-white shadow-xs"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              <IconComponent className="h-4 w-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content 1: Sales & Orders */}
      {activeTab === "SALES" && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <h3 className="font-display font-bold text-lg text-foreground">
              Recent Sales & Order Performance ({filteredOrders.length})
            </h3>

            {filteredOrders.length === 0 ? (
              <p className="text-xs text-muted-foreground py-6 text-center">
                No orders recorded for the selected time period.
              </p>
            ) : (
              <div className="divide-y border border-border rounded-xl overflow-hidden text-xs">
                {filteredOrders.map((o) => (
                  <div
                    key={o.id}
                    className="p-3.5 flex items-center justify-between hover:bg-secondary/20 transition-colors"
                  >
                    <div>
                      <span className="font-mono font-bold text-foreground">{o.id}</span>
                      <p className="text-muted-foreground text-[11px] mt-0.5">
                        {o.customerName} • {o.items.length} item(s)
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-mono font-bold text-emerald-800 text-xs">
                        ₹{o.total}
                      </span>
                      <span className="block text-[10px] uppercase font-bold text-muted-foreground mt-0.5">
                        {o.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content 2: Product Performance */}
      {activeTab === "PRODUCTS" && (
        <div className="space-y-4">
          <DataTable
            data={productPerformance}
            columns={productColumns}
            keyExtractor={(row) => row.name}
            searchPlaceholder="Search product name or category..."
            searchField={(row) => `${row.name} ${row.category}`}
          />
        </div>
      )}

      {/* Tab Content 3: GST & Tax Summary */}
      {activeTab === "TAX_GST" && (
        <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
          <div>
            <h3 className="font-display font-bold text-xl text-foreground">
              GST Tax Liability Summary (18% Flat Rate)
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Estimated tax liabilities formatted for monthly/quarterly GSTR-3B filings.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="p-4 bg-secondary/30 rounded-xl border border-border">
              <span className="eyebrow text-[10px] text-muted-foreground block font-bold">
                Gross Invoiced Sales
              </span>
              <span className="font-mono text-lg font-bold text-foreground">
                ₹{gstBreakdown.grossRevenue.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="p-4 bg-secondary/30 rounded-xl border border-border">
              <span className="eyebrow text-[10px] text-muted-foreground block font-bold">
                Net Taxable Amount
              </span>
              <span className="font-mono text-lg font-bold text-foreground">
                ₹{gstBreakdown.taxableAmount.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="p-4 bg-amber-50/60 border border-amber-200 rounded-xl">
              <span className="eyebrow text-[10px] text-amber-800 block font-bold">
                CGST Collection (9%)
              </span>
              <span className="font-mono text-lg font-bold text-amber-900">
                ₹{gstBreakdown.cgst.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="p-4 bg-amber-50/60 border border-amber-200 rounded-xl">
              <span className="eyebrow text-[10px] text-amber-800 block font-bold">
                SGST Collection (9%)
              </span>
              <span className="font-mono text-lg font-bold text-amber-900">
                ₹{gstBreakdown.sgst.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content 4: Inventory Valuation */}
      {activeTab === "INVENTORY" && (
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-xl text-foreground">
            Inventory Asset Valuation Summary
          </h3>

          <div className="divide-y border border-border rounded-xl overflow-hidden text-xs">
            {productsList.map((p) => {
              const stock = p.stockQuantity ?? p.stock ?? (p.inStock ? 50 : 0);
              return (
                <div
                  key={p.id}
                  className="p-3.5 flex items-center justify-between hover:bg-secondary/20 transition-colors"
                >
                  <div>
                    <span className="font-bold text-foreground">{p.name}</span>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{p.category}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-mono font-bold text-emerald-800 text-xs">
                      Valuation: ₹{(p.price * stock).toLocaleString("en-IN")}
                    </span>
                    <span className="block text-[10px] font-bold text-muted-foreground mt-0.5">
                      {stock} units @ ₹{p.price}/unit
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
